import { CalendarRange, RotateCcw } from 'lucide-react'
import type {
  OperatorProjectionFacetDto,
  OperatorTimelineDto,
  OperatorZoom,
} from '../../services/operatorApi'
import type { OperatorFilters as OperatorFilterState } from '../../stores/operatorStore'

interface OperatorFiltersProps {
  timeline: OperatorTimelineDto
  filters: OperatorFilterState
  zoom: OperatorZoom
  onFilterChange: <K extends keyof OperatorFilterState>(
    key: K,
    value: OperatorFilterState[K],
  ) => void
  onZoomChange: (zoom: OperatorZoom) => void
}

function FacetSelect({
  label,
  value,
  facets,
  onChange,
}: {
  label: string
  value: string
  facets: OperatorProjectionFacetDto[]
  onChange: (value: string) => void
}) {
  return (
    <label className="grid gap-1">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 min-w-0 rounded-md border border-border bg-background px-2 text-[11px] text-foreground outline-none transition duration-150 focus-visible:ring-2 focus-visible:ring-primary/50"
      >
        <option value="">All {label.replace(' filter', '').toLowerCase()}s</option>
        {facets.map((facet) => (
          <option key={facet.value} value={facet.value}>
            {facet.label} ({facet.count})
          </option>
        ))}
      </select>
    </label>
  )
}

const zooms: OperatorZoom[] = ['day', 'week', 'month']

export function OperatorFilters({
  timeline,
  filters,
  zoom,
  onFilterChange,
  onZoomChange,
}: OperatorFiltersProps) {
  const clear = () => {
    onFilterChange('organizations', [])
    onFilterChange('teams', [])
    onFilterChange('people', [])
    onFilterChange('eventTypes', [])
    onFilterChange('statuses', [])
    onFilterChange('horizon', 'all')
  }

  return (
    <div className="border-b border-border bg-muted/20 p-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        <FacetSelect
          label="Organization filter"
          value={filters.organizations[0] ?? ''}
          facets={timeline.filterFacets.organizations}
          onChange={(value) => onFilterChange('organizations', value ? [value] : [])}
        />
        <FacetSelect
          label="Team filter"
          value={filters.teams[0] ?? ''}
          facets={timeline.filterFacets.teams}
          onChange={(value) => onFilterChange('teams', value ? [value] : [])}
        />
        <FacetSelect
          label="Person filter"
          value={filters.people[0] ?? ''}
          facets={timeline.filterFacets.people}
          onChange={(value) => onFilterChange('people', value ? [value] : [])}
        />
        <FacetSelect
          label="Event type filter"
          value={filters.eventTypes[0] ?? ''}
          facets={timeline.filterFacets.eventTypes}
          onChange={(value) => onFilterChange('eventTypes', value ? [value] : [])}
        />
        <FacetSelect
          label="Status filter"
          value={filters.statuses[0] ?? ''}
          facets={timeline.filterFacets.statuses}
          onChange={(value) => onFilterChange('statuses', value ? [value] : [])}
        />
        <label className="grid gap-1">
          <span className="sr-only">Time horizon filter</span>
          <select
            aria-label="Time horizon filter"
            value={filters.horizon}
            onChange={(event) => onFilterChange(
              'horizon',
              event.target.value as OperatorFilterState['horizon'],
            )}
            className="h-8 rounded-md border border-border bg-background px-2 text-[11px] text-foreground outline-none transition duration-150 focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <option value="all">Past + future</option>
            <option value="past">Past only</option>
            <option value="future">Future only</option>
          </select>
        </label>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={clear}
          className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[11px] text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          <RotateCcw size={11} aria-hidden="true" />
          Clear filters
        </button>
        <div
          className="inline-flex items-center gap-1 rounded-md border border-border bg-background p-0.5"
          aria-label="Timeline zoom"
        >
          <CalendarRange size={12} className="mx-1 text-muted-foreground" aria-hidden="true" />
          {zooms.map((item) => (
            <button
              key={item}
              type="button"
              aria-label={`${item[0].toUpperCase()}${item.slice(1)} zoom`}
              aria-pressed={zoom === item}
              onClick={() => onZoomChange(item)}
              className={`h-6 rounded px-2 text-[10px] font-semibold uppercase tracking-[0.1em] outline-none transition duration-150 focus-visible:ring-2 focus-visible:ring-primary/50 ${
                zoom === item
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
